// ============================================================
// Tie-In — TriageHistory.jsx
// Run history screen. Shows all triage runs, tier counts,
// expandable email rows per run.
// ============================================================

import { useState, useEffect } from 'react';
import { t } from './triageTheme.js';
import TierPill from './TierPill.jsx';
import { getHistory, triggerRun } from './triageApi.js';

// ── Icon helpers ──────────────────────────────────────────────
const IconClock = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
  </svg>
);
const IconDraft = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 4L3 11l7 3 3 7 7-17z"/>
  </svg>
);
const IconChevron = ({ open }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    style={{ transform: open ? 'rotate(180deg)' : 'none', transition: t.transitions.fast }}>
    <path d="M6 9l6 6 6-6"/>
  </svg>
);
const IconPlay = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M5 3l14 9-14 9V3z"/>
  </svg>
);

// ── Tier count row ────────────────────────────────────────────
function TierCounts({ run }) {
  const tiers = [
    { tier: 'LEAK',      count: run.leak_count },
    { tier: 'EOD',       count: run.eod_count },
    { tier: 'THIS_WEEK', count: run.this_week_count },
    { tier: 'EXPENSE',   count: run.expense_count },
    { tier: 'LOW',       count: run.low_count },
  ].filter(t => t.count > 0);

  if (!tiers.length) {
    return (
      <span style={{ fontSize: '12px', color: t.colors.textMuted, fontFamily: t.fonts.body }}>
        No emails
      </span>
    );
  }

  return (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {tiers.map(({ tier }) => (
        <TierPill key={tier} tier={tier} />
      ))}
    </div>
  );
}

// ── Email item row ────────────────────────────────────────────
function EmailRow({ item }) {
  const hasDraft = item.draft_created && item.draft_id;

  return (
    <div style={{
      padding:   '10px 14px',
      borderTop: `1px solid ${t.colors.border}`,
      background: t.colors.bgElevated,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
        <span style={{ fontSize: '13px', fontWeight: 500, color: t.colors.textPrimary, fontFamily: t.fonts.body }}>
          {item.sender_name || item.sender_email || 'Unknown sender'}
        </span>
        <TierPill tier={item.tier} />
      </div>
      <div style={{ fontSize: '12px', color: t.colors.textSecondary, fontFamily: t.fonts.body, marginBottom: hasDraft ? '6px' : 0,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '90%' }}>
        {item.subject || '(no subject)'}
      </div>
      {hasDraft && (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px',
          fontSize: '11px', color: t.colors.accent, background: t.colors.accentGlow,
          border: `1px solid ${t.colors.accentDim}`, borderRadius: t.radii.pill,
          padding: '2px 8px', fontFamily: t.fonts.body,
        }}>
          <IconDraft />
          Draft ready in Gmail
        </div>
      )}
      {item.processing_error && (
        <div style={{ fontSize: '11px', color: t.colors.danger, marginTop: '4px', fontFamily: t.fonts.mono }}>
          {item.processing_error}
        </div>
      )}
    </div>
  );
}

// ── Run card ──────────────────────────────────────────────────
function RunCard({ run }) {
  const [open, setOpen] = useState(false);
  const [items] = useState(run.triage_items || []);
  const isEmpty = run.emails_found === 0;
  const failed  = run.status === 'failed';

  const timeStr = new Date(run.run_at).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true,
  });

  return (
    <div style={{
      background:   t.colors.bgPanel,
      border:       `1px solid ${failed ? t.colors.danger + '44' : t.colors.border}`,
      borderRadius: t.radii.lg,
      overflow:     'hidden',
      marginBottom: '8px',
    }}>
      <button
        onClick={() => !isEmpty && setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', width: '100%',
          padding: '12px 14px', background: 'none', border: 'none',
          cursor: isEmpty ? 'default' : 'pointer', gap: '10px', textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: t.colors.textMuted, flexShrink: 0 }}>
          <IconClock />
          <span style={{ fontSize: '12px', fontFamily: t.fonts.mono, color: t.colors.textSecondary }}>
            {timeStr}
          </span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {isEmpty ? (
            <span style={{ fontSize: '13px', color: t.colors.textMuted, fontFamily: t.fonts.body }}>
              Inbox checked — no new emails
            </span>
          ) : failed ? (
            <span style={{ fontSize: '13px', color: t.colors.danger, fontFamily: t.fonts.body }}>
              Run failed — {run.error_message || 'unknown error'}
            </span>
          ) : (
            <TierCounts run={run} />
          )}
        </div>
        {!isEmpty && (
          <div style={{ color: t.colors.textMuted, flexShrink: 0 }}>
            <IconChevron open={open} />
          </div>
        )}
      </button>
      {open && items.length > 0 && (
        <div>{items.map(item => <EmailRow key={item.id} item={item} />)}</div>
      )}
    </div>
  );
}

// ── Date group header ─────────────────────────────────────────
function DayGroup({ label, runs }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{
        fontSize: '11px', fontWeight: 600, fontFamily: t.fonts.mono,
        color: t.colors.textMuted, letterSpacing: '0.08em',
        textTransform: 'uppercase', marginBottom: '8px', paddingLeft: '2px',
      }}>
        {label}
      </div>
      {runs.map(run => <RunCard key={run.id} run={run} />)}
    </div>
  );
}

// ── Run button ────────────────────────────────────────────────
function RunButton({ onClick, running }) {
  return (
    <button
      onClick={onClick}
      disabled={running}
      style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        padding: '9px 16px',
        background: running ? t.colors.bgPanel : t.colors.accent,
        border: `1px solid ${running ? t.colors.border : t.colors.accent}`,
        borderRadius: t.radii.pill,
        color: running ? t.colors.textMuted : '#fff',
        fontSize: '13px', fontWeight: 600, fontFamily: t.fonts.body,
        cursor: running ? 'not-allowed' : 'pointer',
        transition: t.transitions.fast,
      }}
    >
      <IconPlay />
      {running ? 'Running...' : 'Run Triage'}
    </button>
  );
}

// ── Main component ────────────────────────────────────────────
export default function TriageHistory({ userId, notification }) {
  const [runs,    setRuns]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [page,    setPage]    = useState(1);
  const [total,   setTotal]   = useState(0);
  const [running, setRunning] = useState(false);
  const [runMsg,  setRunMsg]  = useState(null);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    getHistory(userId, page)
      .then(data => {
        setRuns(prev => page === 1 ? data.runs : [...prev, ...data.runs]);
        setTotal(data.total);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [userId, page]);

  async function handleRun() {
    if (!userId || running) return;
    setRunning(true);
    setRunMsg(null);
    try {
      await triggerRun(userId);
      setRunMsg('Triage started — results will appear shortly.');
      setTimeout(() => {
        setPage(1);
        setRunning(false);
        setRunMsg(null);
      }, 5000);
    } catch (err) {
      setRunMsg('Run failed: ' + err.message);
      setRunning(false);
    }
  }

  const grouped = {};
  for (const run of runs) {
    const d         = new Date(run.run_at);
    const today     = new Date();
    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
    let label;
    if (d.toDateString() === today.toDateString())          label = 'Today';
    else if (d.toDateString() === yesterday.toDateString()) label = 'Yesterday';
    else label = d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    if (!grouped[label]) grouped[label] = [];
    grouped[label].push(run);
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '12px 12px 80px' }}>

      {/* Top bar with Run button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
        <RunButton onClick={handleRun} running={running} />
      </div>

      {/* Run status message */}
      {runMsg && (
        <div style={{
          padding: '10px 14px', borderRadius: t.radii.md, marginBottom: '12px',
          background: t.colors.bgPanel, border: `1px solid ${t.colors.border}`,
          fontSize: '13px', color: t.colors.textSecondary, fontFamily: t.fonts.body,
        }}>
          {runMsg}
        </div>
      )}

      {/* Notification banner */}
      {notification && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '10px',
          padding: '10px 12px', borderRadius: t.radii.md, marginBottom: '16px',
          background: notification.priority === 'urgent' ? t.colors.leakDim
            : notification.priority === 'high' ? t.colors.eodDim : t.colors.bgPanel,
          border: `1px solid ${notification.priority === 'urgent' ? t.colors.leakBorder
            : notification.priority === 'high' ? t.colors.eodBorder : t.colors.border}`,
        }}>
          <div style={{ fontSize: '13px', color: t.colors.textPrimary, fontFamily: t.fonts.body, lineHeight: 1.4 }}>
            {notification.message}
          </div>
        </div>
      )}

      {loading && page === 1 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: t.colors.textMuted, fontFamily: t.fonts.body, fontSize: '14px' }}>
          Loading...
        </div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: t.colors.danger, fontFamily: t.fonts.body, fontSize: '14px' }}>
          {error}
        </div>
      ) : runs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>📭</div>
          <div style={{ fontSize: '15px', fontWeight: 500, color: t.colors.textPrimary, fontFamily: t.fonts.body, marginBottom: '6px' }}>
            No runs yet
          </div>
          <div style={{ fontSize: '13px', color: t.colors.textSecondary, fontFamily: t.fonts.body, lineHeight: 1.5 }}>
            Hit Run Triage above to process your inbox now.
          </div>
        </div>
      ) : (
        <>
          {Object.entries(grouped).map(([label, dayRuns]) => (
            <DayGroup key={label} label={label} runs={dayRuns} />
          ))}
          {runs.length < total && (
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={loading}
              style={{
                display: 'block', width: '100%', padding: '12px',
                background: 'none', border: `1px solid ${t.colors.border}`,
                borderRadius: t.radii.md, color: t.colors.textSecondary,
                fontSize: '13px', fontFamily: t.fonts.body, cursor: 'pointer', marginTop: '8px',
              }}
            >
              {loading ? 'Loading...' : 'Load more'}
            </button>
          )}
        </>
      )}
    </div>
  );
}
